import { useEffect, useMemo, useState } from "react";
import { IMenuOption } from "types/global";
import { IAccessPermission } from "types/Auth/role";
import { useBillingNoteStatus } from "../DeliveryTrip/use-delivery-trip-check-billing-note";

/**
 * DT Options menu (86d37db3t).
 *
 * The menu is now the same for every non-cancelled status:
 *   แก้ไข / คัดลอก / ยกเลิก / บันทึกวันจ่าย / สร้างใบแจ้งซ่อม
 *
 * `ยกเลิก` documents stay terminal — the menu shrinks to `คัดลอก` only.
 * `payment_status` no longer branches the menu (paymentHandler stopped
 * writing it); the payment flow is a plain quick-entry action.
 */
export const useDeliveryTripOption = (
  status?: string | null,
  isEditing?: boolean,
  deliveryTripPermissions?: IAccessPermission | null,
  uniqueId?: string,
  maintenancePermissions?: IAccessPermission | null,
) => {
  const defaultOptions: IMenuOption[] = useMemo(
    () =>
      [
        deliveryTripPermissions?.update && {
          value: "แก้ไข",
          disabled: isEditing,
        },
        deliveryTripPermissions?.create && {
          value: "คัดลอก",
          disabled: false,
        },
        deliveryTripPermissions?.cancel && {
          value: "ยกเลิก",
          disabled: false,
        },
        deliveryTripPermissions?.update && {
          value: "บันทึกวันจ่าย",
          disabled: isEditing,
        },
        maintenancePermissions?.create && {
          value: "สร้างใบแจ้งซ่อม",
          disabled: isEditing,
        },
      ] as IMenuOption[],
    [isEditing, deliveryTripPermissions, maintenancePermissions]
  );

  const [selectModifyOptions, setSelectedModifyOptions] =
    useState<IMenuOption[]>(defaultOptions);

  const isCancellable = useBillingNoteStatus(uniqueId, status);

  useEffect(() => {
    if (status === "cancelled") {
      // Cancelled documents are terminal — only allow copy.
      setSelectedModifyOptions([defaultOptions[1]].filter(Boolean));
      return;
    }
    // Every other status shows the full menu, with `ยกเลิก` gated when the
    // document has an attached billing note.
    const options = defaultOptions.filter(Boolean);
    setSelectedModifyOptions(
      isCancellable
        ? options
        : options.filter((opt) => opt.value !== "ยกเลิก")
    );
  }, [defaultOptions, isCancellable, status]);

  return selectModifyOptions;
};
